import {
    AccessTimeOutlined,
    AssignmentOutlined,
    ConfirmationNumberOutlined,
    DescriptionOutlined,
    DrawOutlined,
    LockOutlined,
    PaymentsOutlined,
    PersonOutlined,
} from '@mui/icons-material';
import { Box, Link as MuiLink, Stack, Typography } from '@mui/material';
import type { ReactElement } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { buildChangeEntries, formatChangeValue, humanizeEventLabel } from 'helpers/fileHistory';
import useTranslate from 'hooks/useTranslate';
import type { ActivityChange, ClientHistoryEvent, HistoryTimelineEvent } from 'types';
import { formatDateTime } from 'utils/dateUtils';

const translatedEventLabels: Record<string, string> = {
    'candidate.created': 'clientHistoryEventCreated',
    'client.created': 'clientHistoryEventCreated',
    'user.created': 'clientHistoryEventCreated',
    'candidate.invited': 'clientHistoryEventInvited',
    'client.invited': 'clientHistoryEventInvited',
    'user.invited': 'clientHistoryEventInvited',
    'candidate.registered': 'clientHistoryEventRegistered',
    'user.registered': 'clientHistoryEventRegistered',
    'candidate.password_changed': 'clientHistoryEventPasswordChanged',
    'user.password_changed': 'clientHistoryEventPasswordChanged',
    'password.changed': 'clientHistoryEventPasswordChanged',
    'assessment.completed': 'clientHistoryEventAssessmentCompleted',
    'profile.updated': 'clientHistoryEventProfileUpdated',
    'agreement.created': 'clientHistoryEventAgreementCreated',
    'agreement.generated': 'clientHistoryEventAgreementGenerated',
    'agreement.signed': 'clientHistoryEventAgreementSigned',
    'application.created': 'clientHistoryEventApplicationCreated',
    'application.submitted': 'clientHistoryEventApplicationSubmitted',
    'application.result_recorded': 'clientHistoryEventApplicationResult',
    'application.client_status_changed': 'clientHistoryEventStatusChanged',
};

interface FileHistoryEventProps {
    event: HistoryTimelineEvent;
    clientId?: number | null;
}

const isClientHistoryEvent = (event: HistoryTimelineEvent): event is ClientHistoryEvent =>
    'details' in event;

const getClientHistoryCategory = (eventType: string): string => {
    if (eventType.startsWith('agreement.')) return 'AGREEMENT';
    if (eventType.startsWith('application.') || eventType.startsWith('assessment.')) {
        return 'APPLICATION';
    }
    if (/invited|registered|password/i.test(eventType)) {
        return 'AUTH';
    }
    return 'USER';
};

const categoryIcon = (category: string): ReactElement => {
    const iconSx = { fontSize: 18 };

    switch (category) {
        case 'DOCUMENT':
            return <DescriptionOutlined sx={iconSx} />;
        case 'APPLICATION':
            return <AssignmentOutlined sx={iconSx} />;
        case 'BILLING':
            return <PaymentsOutlined sx={iconSx} />;
        case 'AUTH':
            return <LockOutlined sx={iconSx} />;
        case 'USER':
            return <PersonOutlined sx={iconSx} />;
        case 'AGREEMENT':
            return <DrawOutlined sx={iconSx} />;
        case 'TICKET':
            return <ConfirmationNumberOutlined sx={iconSx} />;
        default:
            return <AccessTimeOutlined sx={iconSx} />;
    }
};

const formatActorRole = (role: string): string =>
    role
        .split(/[_\s-]+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(' ');

const FileHistoryEvent = ({ event, clientId }: FileHistoryEventProps) => {
    const { translate } = useTranslate();
    const isClientEvent = isClientHistoryEvent(event);
    const timestamp = isClientEvent ? event.occurred_at : event.timestamp;
    const category = isClientEvent ? getClientHistoryCategory(event.event_type) : event.category;
    const actorName = event.actor_name;
    const actorRole = event.actor_role;
    const message = isClientEvent ? event.details.message : event.display_message;
    const changes = isClientEvent ? event.details.changes : event.changes;
    const meta = isClientEvent ? event.details.meta : event.meta;
    const applicationId = isClientEvent ? event.application_id : null;
    const agreementId = isClientEvent ? event.agreement_id : null;
    const translatedEventLabel = translatedEventLabels[event.event_type];
    const program = typeof meta?.program === 'string' ? meta.program : null;
    const result = !isClientEvent && typeof meta?.result === 'string' ? meta.result : null;
    const visaResult = changes?.visa_result?.to;
    const statusResult = changes?.status?.to;
    const visaDetails = meta?.visa;
    const hasApplicationResult = meta?.result !== undefined && meta.result !== null;
    const visibleChanges: Record<string, ActivityChange> = {};

    Object.entries(changes ?? {}).forEach(([field, change]) => {
        if (isClientEvent && field === 'visa_result' && visaResult !== undefined) return;
        if (isClientEvent && field === 'status' && hasApplicationResult) return;
        visibleChanges[field] = change;
    });

    const title =
        message && message.trim()
            ? message
            : translatedEventLabel
              ? translate(translatedEventLabel)
              : humanizeEventLabel(event.event_type);
    const changeLines = buildChangeEntries(visibleChanges).map(
        (change) => `${change.field}: ${change.from} → ${change.to}`
    );
    const day = formatDateTime(timestamp, 'D MMM');
    const time = formatDateTime(timestamp, 'HH:mm');
    const actorDescription = [actorName, actorRole ? formatActorRole(actorRole) : null]
        .filter(Boolean)
        .join(' · ');
    const applicationLink =
        clientId && applicationId !== null
            ? `/dashboard/${clientId}/client-profile/${applicationId}/documents`
            : null;
    const agreementLink = clientId
        ? `/dashboard/${clientId}/client-profile/agreement${
              applicationId !== null ? `/${applicationId}` : ''
          }`
        : null;

    return (
        <Box
            component="li"
            sx={{
                display: 'grid',
                gridTemplateColumns: '72px 24px minmax(0, 1fr)',
                alignItems: 'start',
                gap: 1.5,
                px: 1.75,
                py: 1.5,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'rgba(148, 163, 184, 0.18)',
                bgcolor: 'rgba(255, 255, 255, 0.92)',
            }}
        >
            <Stack spacing={0} sx={{ lineHeight: 1.3 }}>
                <Typography
                    variant="caption"
                    sx={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: 'primary.main',
                        textTransform: 'capitalize',
                    }}
                >
                    {day}
                </Typography>
                {time !== '—' ? (
                    <Typography
                        variant="caption"
                        sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary' }}
                    >
                        {time}
                    </Typography>
                ) : null}
            </Stack>

            <Box
                aria-hidden
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'primary.main',
                    mt: '1px',
                }}
            >
                {categoryIcon(category)}
            </Box>

            <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                <Typography
                    variant="body2"
                    sx={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: 'text.primary',
                        lineHeight: 1.4,
                        wordBreak: 'break-word',
                    }}
                >
                    {title}
                </Typography>
                {actorDescription ? (
                    <Typography
                        variant="body2"
                        sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.4 }}
                    >
                        {translate('performedBy')}: {actorDescription}
                    </Typography>
                ) : null}
                {program ? (
                    <Typography
                        variant="body2"
                        sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.4 }}
                    >
                        {translate('program')}: {program}
                    </Typography>
                ) : null}
                {result ? (
                    <Typography
                        variant="body2"
                        sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.4 }}
                    >
                        {translate('result')}: {translate(result)}
                    </Typography>
                ) : null}
                {isClientEvent && visaResult !== undefined ? (
                    <Typography
                        variant="body2"
                        sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.4 }}
                    >
                        {translate('visaResult')}: {formatChangeValue(visaResult)}
                    </Typography>
                ) : null}
                {isClientEvent && visaDetails !== undefined && visaDetails !== null ? (
                    <Typography
                        variant="body2"
                        sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.4 }}
                    >
                        {translate('visaDetails')}: {formatChangeValue(visaDetails)}
                    </Typography>
                ) : null}
                {isClientEvent && hasApplicationResult && statusResult !== undefined ? (
                    <Typography
                        variant="body2"
                        sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.4 }}
                    >
                        {translate('status')}: {formatChangeValue(statusResult)}
                    </Typography>
                ) : null}
                {isClientEvent && hasApplicationResult ? (
                    <Typography
                        variant="body2"
                        sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.4 }}
                    >
                        {translate('result')}: {formatChangeValue(meta?.result)}
                    </Typography>
                ) : null}
                {changeLines.map((change) => (
                    <Typography
                        key={change}
                        variant="body2"
                        sx={{
                            fontSize: 13,
                            color: 'text.secondary',
                            lineHeight: 1.45,
                            wordBreak: 'break-word',
                        }}
                    >
                        {change}
                    </Typography>
                ))}
                {applicationLink || (agreementId !== null && agreementLink) ? (
                    <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
                        {applicationLink ? (
                            <MuiLink
                                component={RouterLink}
                                to={applicationLink}
                                variant="caption"
                                underline="hover"
                            >
                                {translate('case')} #{applicationId}
                            </MuiLink>
                        ) : null}
                        {agreementId !== null && agreementLink ? (
                            <MuiLink
                                component={RouterLink}
                                to={agreementLink}
                                variant="caption"
                                underline="hover"
                            >
                                {translate('agreement')} #{agreementId}
                            </MuiLink>
                        ) : null}
                    </Stack>
                ) : null}
            </Stack>
        </Box>
    );
};

export default FileHistoryEvent;
